import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
} from '@angular/core';

import {
  Icon,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableDataPayload,
  TableService,
} from '@web/shared/ui';
import { WarehouseReceiptListAction } from '../../enums/warehouse-receipt-list-action';
import { WarehouseReceiptCollectionFilter } from '../../models/warehouse-receipt-collection-filter';
import { WarehouseReceiptCollectionItem } from '../../models/warehouse-receipt-collection-item';
import { WarehouseReceiptListItemAction } from '../../enums/warehouse-receipt-list-item-action';
import { Route, RouteParam } from '@web/common';
import { Observable, of } from 'rxjs';
import { WarehouseReceiptDataSourceService } from '../../services/warehouse-receipt-data-source.service';
import { WarehouseReceiptListCustomCellTemplateComponent } from '../../components/warehouse-receipt-list-custom-cell-template/warehouse-receipt-list-custom-cell-template.component';
import { WarehouseReceiptStatusProviderService } from '../../providers/warehouse-receipt-status-provider.service';
import { once } from '@web/utils';

@Component({
  selector: 'app-warehouse-receipt-list',
  imports: [TableComponent, WarehouseReceiptListCustomCellTemplateComponent],
  templateUrl: './warehouse-receipt-list.component.html',
  styleUrl: './warehouse-receipt-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WarehouseReceiptListComponent {
  /* prettier-ignore */
  private readonly tableBuilder = inject(TableBuilderFactory<WarehouseReceiptCollectionItem, WarehouseReceiptCollectionFilter, WarehouseReceiptListItemAction, never, WarehouseReceiptListAction>).create();
  private readonly table = inject(TableService);
  /* prettier-ignore */
  private readonly statusProvider = inject(WarehouseReceiptStatusProviderService);
  /* prettier-ignore */
  private readonly warehouseReceiptDataSource = inject(WarehouseReceiptDataSourceService);

  public readonly createReceiptVisibility = input<boolean>(false);

  protected readonly config = this.buildTable();

  private buildTable(): TableConfig<
    WarehouseReceiptCollectionItem,
    WarehouseReceiptCollectionFilter,
    WarehouseReceiptListItemAction,
    never,
    WarehouseReceiptListAction
  > {
    return this.tableBuilder
      .dataKey('id')
      .columns((builder) => [
        builder //
          .initText('number')
          .filterKey('number')
          .sortKey('number')
          .filterable(true)
          .sortable(true)
          .label('Numer przyjęcia')
          .build(),

        builder
          .initDate('receiptedAt')
          .filterKey('receiptedAt')
          .sortKey('receiptedAt')
          .width('13rem')
          .filterable(true)
          .sortable(true)
          .label('Data przyjęcia')
          .build(),

        builder
          .initCustom('productNames')
          .filterKey('goodsNames')
          .sortKey('goodsNames')
          .width('70rem')
          .filterable(true)
          .sortable(true)
          .label('Nazwy towarów')
          .build(),

        builder
          .initDate('createdAt')
          .filterKey('createdAt')
          .sortKey('createdAt')
          .filterable(true)
          .sortable(true)
          .label('Data utworzenia')
          .build(),

        builder
          .initCustom('status')
          .filterKey('status')
          .width('20rem')
          .sortKey('status')
          .filterable(true)
          .sortable(true)
          .label('Status')
          .build(),
      ])
      .actions((builder) => [
        builder
          .initRouterLink(WarehouseReceiptListAction.Create)
          .routerLink((provider) =>
            provider.routerLink(Route.WarehouseReceiptCreate),
          )
          .label('Dodaj przyjęcie')
          .visibility(() => of(this.createReceiptVisibility()))
          .build(),
      ])
      .filters((builder) => [
        builder //
          .initText('number')
          .build(),

        builder //
          .initDateRange('createdAt')
          .build(),

        builder
          .initSelect('status')
          .dictionary(() => this.statusProvider.selectItems())
          .build(),

        builder //
          .initText('goodsNames')
          .build(),

        builder //
          .initText('goodsSkus')
          .build(),

        builder //
          .initDateRange('receiptedAt')
          .build(),
      ])
      .itemActions((builder) => [
        builder
          .initRouterLink(WarehouseReceiptListItemAction.Preview)
          .visibility((item) => item.canBeViewed)
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.WarehouseReceiptDetails, {
                [RouteParam.Guid]: item.id,
              }),
          )
          .tooltip('Podgląd')
          .icon(Icon.Eye)
          .build(),

        builder
          .initRouterLink(WarehouseReceiptListItemAction.Edit)
          .visibility((item) => item.canBeEdited)
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.WarehouseReceiptEdit, {
                [RouteParam.Guid]: item.id,
              }),
          )
          .tooltip('Edytuj')
          .icon(Icon.Pencil)
          .build(),

        builder
          .initCommand(WarehouseReceiptListItemAction.Approve)
          .visibility((item) => item.canBeApproved)
          .command((item) => this.approve(item))
          .tooltip('Zatwierdź')
          .icon(Icon.CheckCircle)
          .build(),
      ])
      .dataSource({
        fetch: (
          lazyLoad,
        ): Observable<TableDataPayload<WarehouseReceiptCollectionItem>> =>
          this.warehouseReceiptDataSource.fetchCollection(lazyLoad),
      })
      .build();
  }

  private approve(item: WarehouseReceiptCollectionItem): void {
    this.warehouseReceiptDataSource
      .approveById(item.id)
      .pipe(once())
      .subscribe({
        next: () => this.refresh(),
      });
  }

  private refresh(): void {
    this.table.refresh();
  }
}
